import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CircleCheck, CircleX, ClipboardCheck, Clock, MessageSquare, PackageCheck, Send, ShoppingCart } from 'lucide-react';
import { countWindow, formatDateTime, formatDay, todayUk, weekLabel } from '@nu/shared';
import { sendCount, useMyStock } from './store.js';
import ItemGroups from './ItemGroups.jsx';
import { useDraft } from '../../offline/draft.js';
import { ORDER_STATUS, useMyOrders } from './orders.js';

// Field: /stock (a button per job) and /stock/count. The weekly UT count:
// one count per team, due Friday to Monday (it counts as the new week) but
// allowed any day. Works with no signal (the count waits in the outbox).
// Orders from Nano Fibre storerooms are the second button (Orders.jsx).
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const dayName = (day) => DAY_NAMES[new Date(`${day}T12:00:00Z`).getUTCDay()];
const addDays = (day, n) => new Date(new Date(`${day}T12:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);

// Under the header: in the window, on time or late (Tuesday to Thursday is
// late for the week running), as short "Due by / Was due / Next" lines.
const short = (day) => `${dayName(day).slice(0, 3)} ${formatDay(day).slice(0, 5)}`;
const Span = ({ from, to }) => <strong>{short(from)} – {short(to)}</strong>;

function TimingFlag({ w, countedAt }) {
  const day = countedAt ? todayUk(new Date(countedAt)) : todayUk();
  const late = day > w.closes;
  const tag = late ? 'Late' : countedAt ? 'On time' : 'In the window';
  const rows = [];
  if (countedAt) rows.push(['Counted', <strong>{short(day)}</strong>]);
  if (late) rows.push(['Was due', <Span from={w.opens} to={w.closes} />]);
  else if (!countedAt) rows.push(['Due by', <strong>{short(w.closes)}</strong>]);
  if (late || countedAt) rows.push(['Next', <><strong>{weekLabel(addDays(w.week, 7))}</strong>, <Span from={addDays(w.opens, 7)} to={addDays(w.closes, 7)} /></>]);
  return (
    <div className={`stock-flag ${late ? 'late' : 'ok'}`} role="status">
      <span className="stock-flag-tag">{late ? <CircleX size={16} aria-hidden="true" /> : <CircleCheck size={16} aria-hidden="true" />}{tag}</span>
      <dl className="stock-flag-rows small">
        {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
    </div>
  );
}

// The count week from the phone's own date, so it's right with no signal too.
function useWindow(data) {
  return useMemo(() => {
    const w = countWindow(todayUk());
    return { ...w, weekLabel: weekLabel(w.week), stale: Boolean(data && data.window.week !== w.week) };
  }, [data]);
}

// /stock: a square button per job; each shows where it stands.
export default function Stock() {
  const { data, pending } = useMyStock();
  const w = useWindow(data);
  const count = w.stale ? null : (pending || data?.count);
  // The week a count now is for, and a label with a green tick (Counted) or
  // red cross (Not counted); the border matches.
  const mark = !data?.team || pending ? null : count ? 'ok' : 'bad';
  const label = !data ? '' : !data.team ? 'No team' : pending ? 'Sending…' : count ? 'Counted' : 'Not counted';

  return (
    <div className="stack">
      <h1 className="stock-title">Stock</h1>
      <div className="stock-tiles">
        <Link to="/stock/count" className={`stock-tile${mark ? ` ${mark}` : ''}`}>
          <span className="stock-tile-icon" aria-hidden="true"><ClipboardCheck size={26} /></span>
          <strong>UT Stock Count</strong>
          {data?.team && <span className="small">{w.weekLabel}</span>}
          {label && (
            <span className={`stock-state${mark ? ` ${mark}` : ''}`}>
              {mark === 'ok' && <CircleCheck size={15} aria-hidden="true" />}
              {mark === 'bad' && <CircleX size={15} aria-hidden="true" />}
              {label}
            </span>
          )}
        </Link>
        <OrdersTile />
      </div>
    </div>
  );
}

// The team's order: its number and where it's got to (green once it's ready
// to collect). "Coming soon" until orders are switched on.
function OrdersTile() {
  const { data, order, sending } = useMyOrders();
  if (!data?.live) {
    return (
      <div className="stock-tile off" aria-disabled="true">
        <span className="stock-tile-icon" aria-hidden="true"><ShoppingCart size={26} /></span>
        <strong>Orders</strong>
        <span className="small">Coming soon</span>
      </div>
    );
  }
  const s = order && ORDER_STATUS[order.status];
  const live = order && ['ordered', 'picked', 'handed_over'].includes(order.status);
  const Icon = s?.tone === 'ok' ? PackageCheck : s?.tone === 'wait' ? Clock : CircleCheck;
  return (
    <Link to={order || data.history?.length ? '/stock/order' : '/stock/order/edit'} className={`stock-tile${live ? ` ${s.tone}` : ''}`}>
      <span className="stock-tile-icon" aria-hidden="true"><ShoppingCart size={26} /></span>
      <strong>Orders</strong>
      {data.team && <span className="small">{live ? (order.ref || 'New order') : 'No open order'}</span>}
      {live && <span className={`stock-state ${s.tone}`}><Icon size={15} aria-hidden="true" />{sending ? 'Sending…' : s.label}</span>}
    </Link>
  );
}

// /stock/count: the count for the week, straight from the Stock button.
// Already counted: who and when, with their numbers filled in; sending
// again replaces it.
export function StockCount({ me }) {
  const navigate = useNavigate();
  const { data, status, pending, refused } = useMyStock();
  const w = useWindow(data);
  const done = w.stale ? null : (pending || data?.count);
  const items = data?.items || [];
  const start = useMemo(() => Object.fromEntries((done?.lines || []).map((l) => [l.itemId, String(l.qty)])), [done]);
  // What's typed so far is kept on the phone until it's sent.
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.count.${w.week}`);
  const [busy, setBusy] = useState(false);
  const values = draft.qty ?? start;
  const note = draft.note ?? '';
  const set = (id, v) => saveDraft({ qty: { ...values, [id]: v } });
  const filled = (id) => values[id] !== undefined && values[id] !== '';
  const ready = items.length > 0 && items.every((i) => filled(i.id));
  const filledCount = items.filter((i) => filled(i.id)).length;

  const back = <button type="button" className="link-btn back-link" onClick={() => navigate('/stock')}><ArrowLeft size={18} aria-hidden="true" /> Stock</button>;
  if (!data) return <section className="card">{back}<p className="muted">{status === 'error' ? 'Your stock can’t be loaded with no signal yet. Try again when you have signal.' : 'Loading…'}</p></section>;
  if (!data.team) return <section className="card">{back}<p>You aren’t in a team, so there’s nothing to count. The office puts you in a team on Match-Ups.</p></section>;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    await sendCount(me, w.week, items, values, note.trim());
    clearDraft();
    navigate('/stock');
  };

  return (
    <section className="card">
      {back}
      <h1 className="stock-week">{w.weekLabel} UT count</h1>
      <TimingFlag w={w} countedAt={done ? (pending ? pending.at : done.countedAt) : null} />
      {refused && <p className="notice-line notice-bad" role="alert">{refused}</p>}
      {done && <p className="small"><strong>{pending ? 'Sending…' : 'Counted'}</strong> by {pending ? pending.byName : done.countedByName}, {formatDateTime(pending ? pending.at : done.countedAt)}. Their numbers are below; submit again to replace the count.</p>}
      {items.length > 0 && (
        <div className="stock-progress">
          <span className={`stock-state ${ready ? 'ok' : 'bad'}`}>{ready ? <CircleCheck size={15} aria-hidden="true" /> : <CircleX size={15} aria-hidden="true" />}{filledCount} of {items.length} counted</span>
        </div>
      )}
      <p className="muted small">{data.team.name}: one count for the team, either of you can do it. Count what’s on the van now, including 0.</p>
      {!items.length && <p>Nothing is set up to be counted yet.</p>}
      <form onSubmit={submit} className="stack">
        <ItemGroups items={items} values={values} onChange={set} done={filled} tally={(g) => `${g.filter((x) => filled(x.id)).length} of ${g.length}`} whole={(g) => g.every((x) => filled(x.id))} />
        <label className="fld">
          <span className="with-icon"><MessageSquare size={16} aria-hidden="true" /> Note <span className="small muted">(optional)</span></span>
          <textarea rows={2} maxLength={1000} value={note} onChange={(e) => saveDraft({ note: e.target.value })} placeholder="Anything the office should know" />
        </label>
        <div className="actions">
          <button type="submit" className="btn primary with-icon" disabled={!ready || busy}><Send size={18} aria-hidden="true" /> {done ? 'Submit again' : 'Submit count'}</button>
        </div>
        {!ready && items.length > 0 && <p className="small muted">Enter a number for every item (0 if there are none).</p>}
      </form>
    </section>
  );
}
